import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buyerService } from "@/services/buyerService";
import { extractErrorMessage } from "@/utils/errorMessageHandler";
import { toast } from "sonner";
import type { FollowingStore } from "@/types/layout";

interface PaginationData {
  total: number;
  pages: number;
  current: number;
  hasMore: boolean;
}

interface UseFollowingStoresOptions {
  page?: number;
  limit?: number;
  query?: string;
  enabled?: boolean;
}

interface UseFollowingStoresReturn {
  stores: FollowingStore[];
  isLoading: boolean;
  error: Error | null;
  pagination: PaginationData | null;
  refetch: () => void;
  followStore: (sellerId: string) => Promise<void>;
  unfollowStore: (sellerId: string) => Promise<void>;
  toggleFollowStore: (sellerId: string, isCurrentlyFollowing: boolean) => Promise<void>;
  isFollowing: boolean;
  isUnfollowing: boolean;
  isToggling: boolean;
  followError: string | null;
  unfollowError: string | null;
}

const FOLLOWING_STORES_KEY = "following-stores";

// Query keys for seller profiles (to update seller profile pages)
// Must match the keys used in useBuyerSellerProfile
const sellerKeys = {
  all: ["seller"] as const,
  profile: (sellerId: string) => [...sellerKeys.all, "profile", sellerId] as const,
};

// Query key for suggested sellers (to update suggested sellers on home page)
const SUGGESTED_SELLERS_KEY = "suggested";

/**
 * Custom hook to manage following stores with TanStack Query and optimistic updates
 *
 * @param options - Configuration options
 * @example
 * ```typescript
 * // Fetch first 6 stores for sidebar
 * const { stores, isLoading } = useFollowingStores({ limit: 6 });
 *
 * // Fetch with pagination for dialog
 * const { stores, pagination } = useFollowingStores({ page: 1, limit: 20 });
 * 
 * // Use for optimistic updates only (e.g., in search results or seller profiles)
 * const { toggleFollowStore } = useFollowingStores({ enabled: false });
 * ```
 */
export function useFollowingStores(
  options: UseFollowingStoresOptions = {}
): UseFollowingStoresReturn {
  const { page = 1, limit = 20, query, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch following stores
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [FOLLOWING_STORES_KEY, page, limit, query || ''],
    queryFn: () => buyerService.getFollowingStores({ page, limit, query }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (cacheTime)
    retry: (failureCount, error) => {
      // Don't retry on authentication errors (401, 403)
      const errorMessage = extractErrorMessage(error as Error);
      if (
        errorMessage.toLowerCase().includes("unauthorized") ||
        errorMessage.toLowerCase().includes("unauthenticated") ||
        errorMessage.toLowerCase().includes("forbidden")
      ) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

  // Toggle follow/unfollow mutation with comprehensive optimistic updates
  const toggleFollowMutation = useMutation({
    mutationFn: async ({ sellerId, isCurrentlyFollowing }: { sellerId: string; isCurrentlyFollowing: boolean }) => {
      if (isCurrentlyFollowing) {
        return buyerService.unfollowStore(sellerId);
      } else {
        return buyerService.followStore(sellerId);
      }
    },
    onMutate: async ({ sellerId, isCurrentlyFollowing }) => {
      // Cancel outgoing refetches for following stores, seller profile, and suggested sellers
      await queryClient.cancelQueries({ queryKey: [FOLLOWING_STORES_KEY] });
      await queryClient.cancelQueries({ queryKey: sellerKeys.profile(sellerId) });
      await queryClient.cancelQueries({ queryKey: [SUGGESTED_SELLERS_KEY, "sellers"] });

      // Snapshot previous values for rollback
      const previousFollowingData = queryClient.getQueryData([FOLLOWING_STORES_KEY, page, limit]);
      const previousProfileData = queryClient.getQueryData(sellerKeys.profile(sellerId));
      const previousSuggestedSellers = queryClient.getQueryData([SUGGESTED_SELLERS_KEY, "sellers", 10]);

      // Optimistically update following stores lists
      queryClient.setQueriesData(
        { queryKey: [FOLLOWING_STORES_KEY] },
        (old: any) => {
          if (!old) return old;
          
          return {
            ...old,
            stores: old.stores?.map((store: FollowingStore) =>
              store.id === sellerId ? { ...store, isFollowing: !isCurrentlyFollowing } : store
            ),
          };
        }
      );

      // Optimistically update seller profile if it exists
      queryClient.setQueryData(
        sellerKeys.profile(sellerId),
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              storeInfo: {
                ...old.data.storeInfo,
                isFollowing: !isCurrentlyFollowing,
              },
              stats: {
                ...old.data.stats,
                followersCount: isCurrentlyFollowing
                  ? old.data.stats.followersCount - 1
                  : old.data.stats.followersCount + 1,
              },
            },
          };
        }
      );

      // Optimistically update suggested sellers cache
      queryClient.setQueriesData(
        { queryKey: [SUGGESTED_SELLERS_KEY, "sellers"] },
        (old: any) => {
          if (!old || !old.sellers) return old;
          
          return {
            ...old,
            sellers: old.sellers.map((seller: any) =>
              seller._id === sellerId
                ? { ...seller, isFollowing: !isCurrentlyFollowing }
                : seller
            ),
          };
        }
      );

      // Return context with previous data for potential rollback
      return { previousFollowingData, previousProfileData, previousSuggestedSellers };
    },
    onError: (error: Error, { sellerId, isCurrentlyFollowing }, context) => {
      // Rollback following stores list
      if (context?.previousFollowingData) {
        queryClient.setQueryData(
          [FOLLOWING_STORES_KEY, page, limit],
          context.previousFollowingData
        );
      }

      // Rollback seller profile
      if (context?.previousProfileData) {
        queryClient.setQueryData(
          sellerKeys.profile(sellerId),
          context.previousProfileData
        );
      }

      // Rollback suggested sellers
      if (context?.previousSuggestedSellers) {
        queryClient.setQueryData(
          [SUGGESTED_SELLERS_KEY, "sellers", 10],
          context.previousSuggestedSellers
        );
      }
      
      const errorMessage = extractErrorMessage(error);
      const action = isCurrentlyFollowing ? 'unfollow' : 'follow';
      console.error(`Error ${action}ing store:`, errorMessage);
      toast.error(`Failed to ${action} store: ${errorMessage}`);
    },
    onSuccess: (data, { isCurrentlyFollowing }) => {
      const action = isCurrentlyFollowing ? 'Unfollowed' : 'Followed';
      const message = data?.message || `${action} store`;
      toast.success(message);
    },
    onSettled: (data, error, { sellerId }) => {
      // Refetch to ensure consistency with server state
      queryClient.invalidateQueries({ queryKey: [FOLLOWING_STORES_KEY] });
      queryClient.invalidateQueries({ queryKey: sellerKeys.profile(sellerId) });
      queryClient.invalidateQueries({ queryKey: [SUGGESTED_SELLERS_KEY, "sellers"] });
    },
  });

  // Follow store mutation (legacy support)
  const followMutation = useMutation({
    mutationFn: (sellerId: string) => buyerService.followStore(sellerId),
    onMutate: async (sellerId: string) => {
      await queryClient.cancelQueries({ queryKey: [FOLLOWING_STORES_KEY] });
      await queryClient.cancelQueries({ queryKey: sellerKeys.profile(sellerId) });

      const previousFollowingData = queryClient.getQueryData([FOLLOWING_STORES_KEY, page, limit]);
      const previousProfileData = queryClient.getQueryData(sellerKeys.profile(sellerId));

      queryClient.setQueriesData(
        { queryKey: [FOLLOWING_STORES_KEY] },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            stores: old.stores?.map((store: FollowingStore) =>
              store.id === sellerId ? { ...store, isFollowing: true } : store
            ),
          };
        }
      );

      queryClient.setQueryData(
        sellerKeys.profile(sellerId),
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              storeInfo: {
                ...old.data.storeInfo,
                isFollowing: true,
              },
              stats: {
                ...old.data.stats,
                followersCount: old.data.stats.followersCount + 1,
              },
            },
          };
        }
      );

      return { previousFollowingData, previousProfileData };
    },
    onSuccess: (data) => {
      const message = data?.message || 'Followed store';
      toast.success(message);
    },
    onError: (error: Error, sellerId: string, context) => {
      if (context?.previousFollowingData) {
        queryClient.setQueryData(
          [FOLLOWING_STORES_KEY, page, limit],
          context.previousFollowingData
        );
      }
      if (context?.previousProfileData) {
        queryClient.setQueryData(
          sellerKeys.profile(sellerId),
          context.previousProfileData
        );
      }
      
      const errorMessage = extractErrorMessage(error);
      console.error("Error following store:", errorMessage);
      toast.error(`Failed to follow store: ${errorMessage}`);
    },
    onSettled: (data, error, sellerId) => {
      queryClient.invalidateQueries({ queryKey: [FOLLOWING_STORES_KEY] });
      queryClient.invalidateQueries({ queryKey: sellerKeys.profile(sellerId) });
    },
  });

  // Unfollow store mutation (legacy support)
  const unfollowMutation = useMutation({
    mutationFn: (sellerId: string) => buyerService.unfollowStore(sellerId),
    onMutate: async (sellerId: string) => {
      await queryClient.cancelQueries({ queryKey: [FOLLOWING_STORES_KEY] });
      await queryClient.cancelQueries({ queryKey: sellerKeys.profile(sellerId) });

      const previousFollowingData = queryClient.getQueryData([FOLLOWING_STORES_KEY, page, limit]);
      const previousProfileData = queryClient.getQueryData(sellerKeys.profile(sellerId));

      queryClient.setQueriesData(
        { queryKey: [FOLLOWING_STORES_KEY] },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            stores: old.stores?.map((store: FollowingStore) =>
              store.id === sellerId ? { ...store, isFollowing: false } : store
            ),
          };
        }
      );

      queryClient.setQueryData(
        sellerKeys.profile(sellerId),
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              storeInfo: {
                ...old.data.storeInfo,
                isFollowing: false,
              },
              stats: {
                ...old.data.stats,
                followersCount: old.data.stats.followersCount - 1,
              },
            },
          };
        }
      );

      return { previousFollowingData, previousProfileData };
    },
    onSuccess: (data) => {
      const message = data?.message || 'Unfollowed store';
      toast.success(message);
    },
    onError: (error: Error, sellerId: string, context) => {
      if (context?.previousFollowingData) {
        queryClient.setQueryData(
          [FOLLOWING_STORES_KEY, page, limit],
          context.previousFollowingData
        );
      }
      if (context?.previousProfileData) {
        queryClient.setQueryData(
          sellerKeys.profile(sellerId),
          context.previousProfileData
        );
      }
      
      const errorMessage = extractErrorMessage(error);
      console.error("Error unfollowing store:", errorMessage);
      toast.error(`Failed to unfollow store: ${errorMessage}`);
    },
    onSettled: (data, error, sellerId) => {
      queryClient.invalidateQueries({ queryKey: [FOLLOWING_STORES_KEY] });
      queryClient.invalidateQueries({ queryKey: sellerKeys.profile(sellerId) });
    },
  });

  return {
    stores: data?.stores || [],
    isLoading,
    error: error as Error | null,
    pagination: data?.pagination || null,
    refetch,
    followStore: async (sellerId: string) => {
      await followMutation.mutateAsync(sellerId);
    },
    unfollowStore: async (sellerId: string) => {
      await unfollowMutation.mutateAsync(sellerId);
    },
    toggleFollowStore: async (sellerId: string, isCurrentlyFollowing: boolean) => {
      await toggleFollowMutation.mutateAsync({ sellerId, isCurrentlyFollowing });
    },
    isFollowing: followMutation.isPending,
    isUnfollowing: unfollowMutation.isPending,
    isToggling: toggleFollowMutation.isPending,
    followError: followMutation.error
      ? extractErrorMessage(followMutation.error as Error)
      : null,
    unfollowError: unfollowMutation.error
      ? extractErrorMessage(unfollowMutation.error as Error)
      : null,
  };
}